import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addNewCategory,
  deleteCategory,
  getCategories,
  updateCategory,
} from "../../../../api/faceswap/category";
import { BrowseImageIcon, DeleteIcon } from "../../../../assets/icons/icons";
import { useSelectedOption } from "../../../../context/media-context/SelectedOptionContext";
import { handleApiError } from "../../../../utils/common";
import Button from "../../../common/button";
import MessageDisplay from "../../../common/ErrorDisplay";
import WarningModal from "../../../common/warning-modal/WarningModal";
import DialogBox from "../dialogbox";
import SkeletonLoader from "./SkeletonLoader";

export default function EventList() {
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [initialData, setInitialData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [showSaveReset, setShowSaveReset] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const queryClient = useQueryClient();
  const [changedDataArray, setChangedDataArray] = useState([]);
  const { setSelectedOption } = useSelectedOption();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isSavingData, setIsSavingData] = useState(false);
  const pagination = 10;

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["getCategories"],
    queryFn: getCategories,
  });

  data?.sort((a, b) => {
    if (a.category_order < b.category_order) return -1;
    if (a.category_order > b.category_order) return 1;
    return 0;
  });
  const totalPages = Math.ceil(data?.length / pagination);
  const currentIndex = (currentPage - 1) * pagination;
  const endIndex = currentIndex + pagination;

  // const PaginatedData = React.useMemo(() => {
  //   if (data) {
  //     const currentIndex = (currentPage - 1) * pagination;
  //     return data.slice(currentIndex, currentIndex + pagination);
  //   }
  //   return [];
  // }, [data, currentPage, pagination]);

  const deleteCategoryMutation = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(["getCategories"]);
      toast.success("Category deleted successfully!");
    },
    onError: (error) => {
      toast.error(`Error deleting category: ${error.message}`);
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: updateCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(["getCategories"]);
      setShowSaveReset(false);
      setIsEdit(false);
      toast.success("Category updated successfully!");
    },
    onError: (error) => {
      toast.error(`Error updating category: ${error.message}`);
    },
  });

  const addNewCategoryMutation = useMutation({
    mutationFn: addNewCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(["getCategories"]);
      toast.success("Category added successfully!");
    },
    onError: (error) => {
      handleApiError(error);
    },
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (data) {
      setInitialData(data);
      setOriginalData(data);
    }
  }, [data]);

  const handleOpen = () => {
    setIsOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setIsOpenDialog(false);
    setIsEdit(false);
  };

  // Function to check if data has been modified
  const isDataChanged = (newData, originalData) => {
    return JSON.stringify(newData) !== JSON.stringify(originalData);
  };

  // Handles changes in input fields
  const handleInputChange = (id, event) => {
    const { name, type, checked, value } = event.target;
    const updatedData = initialData.map((item) => {
      if (item.category_id === id) {
        let updatedItem = {
          ...item,
          [name]: type === "checkbox" ? checked : value,
        };
        setChangedDataArray((prev) => {
          const filtered = prev.filter((item) => item.category_id !== id);
          return [...filtered, updatedItem];
        });
        return updatedItem;
      }
      return item;
    });

    const isChanged = isDataChanged(updatedData, originalData);

    setInitialData(updatedData);
    setIsEdit(isChanged);
    setShowSaveReset(isChanged);
  };

  const checkDuplicateCategoryOrders = (categories) => {
    const categoryTable = {};
    const duplicateOrders = new Set();
    const emptyOrders = [];

    for (const category of categories) {
      const { category_id, category_order } = category;

      const stringOrder = String(category_order).trim();
      if (!stringOrder) {
        emptyOrders.push(category_id);
      } else if (categoryTable[stringOrder]) {
        duplicateOrders.add(stringOrder);
      } else {
        categoryTable[stringOrder] = category_id;
      }
    }

    if (emptyOrders.length > 0) {
      return {
        isValid: false,
        errorMessage: `Category order cannot be empty`,
      };
    } else {
      return { isValid: true };
    }
  };

  const handleSave = () => {
    const resultOfDuplicate = checkDuplicateCategoryOrders(initialData);

    if (resultOfDuplicate.isValid) {
      try {
        const res = updateCategoryMutation.mutate(payload);
        if (res) {
          setShowSaveReset(false);
          setIsEdit(false);
          payload = [];
        }
      } catch (error) {
        setShowSaveReset(true);
        setIsEdit(true);
        console.error("Error during API call:", error);
      }
    } else {
      setShowSaveReset(true);
      setIsEdit(true);
      toast.warn(resultOfDuplicate.errorMessage);
    }
  };

  // Resets the form data to its original state
  const handleReset = () => {
    setInitialData(originalData);
    setChangedDataArray([]);
    setIsEdit(false);
    setShowSaveReset(false);
  };

  const handleWarningModal = (id) => {
    setDeleteId(id);
    setIsWarningModalOpen(true);
  };

  const handleDelete = () => {
    try {
      deleteCategoryMutation.mutate(deleteId);
      setIsWarningModalOpen(false);
    } catch (error) {}
    setIsWarningModalOpen(false);
  };

  const handleRetry = () => {
    refetch();
  };

  const handleSelectValue = (id) => {
    setSelectedOption(id.category_name);
  };

  if (error) {
    return (
      <MessageDisplay
        type="error"
        message="Failed to load data!"
        onAction={handleRetry}
      />
    );
  }

  let payload = {
    data: changedDataArray.map((item) => ({
      category_id: item.category_id,
      new_order: item.category_order,
      is_top_collection: item.is_top_collection,
    })),
  };

  const handleAddNewCategory = (data, closeDialog) => {
    try {
      addNewCategoryMutation.mutate(data);
      closeDialog();
    } catch (error) {
      console.log("Error adding new category:", error);
      throw new error(error);
    }
  };

  return (
    <div className="relative rounded-[20px] border border-[#0075FF1A] pb-[72px]">
      <div className="my-[20px] flex flex-row items-center justify-between px-[40px]">
        <h2 className="text-left font-outfit text-[20px] font-medium leading-[25.2px] text-[#222222]">
          Get All Categories
        </h2>
        <div className="flex flex-row gap-4">
          <button
            onClick={handleReset}
            className={`rounded-[10px] px-[23px] py-[12px] text-center font-outfit text-[16px] font-medium leading-[20.16px] text-white ${
              isEdit && showSaveReset
                ? "cursor-pointer bg-gray-500 opacity-100"
                : "cursor-not-allowed bg-gray-500 opacity-50"
            }`}
            disabled={!isEdit || !showSaveReset}
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            className={`rounded-[10px] px-[23px] py-[12px] text-center font-outfit text-[16px] font-medium leading-[20.16px] text-white ${
              isEdit && showSaveReset
                ? "cursor-pointer bg-blue-500 opacity-100"
                : "cursor-not-allowed bg-blue-500 opacity-50"
            }`}
            disabled={!isEdit || !showSaveReset}
          >
            Save
          </button>
          {/* )} */}
          <Button handleOpen={handleOpen}>Create New</Button>
        </div>
      </div>
      {data?.data?.length === 0 ? (
        <p>No activity found.</p>
      ) : (
        <div className="scrollbar-hide h-[calc(100vh-350px)] overflow-hidden overflow-y-auto">
          <div className="grid grid-cols-[20px_repeat(4,1fr)] gap-4 bg-[#0075FF1A] px-[40px] py-[15px]">
            <div className="text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]" />
            <div className="text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Category ID
            </div>
            <div className="text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Category Name
            </div>

            <div className="text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Category Order
            </div>
            {/* <div className="text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Delete
            </div> */}
            {/* <div className="text-left font-outfit text-[16px] font-medium leading-[20.16px] text-[#666666]">
              Browse Image
            </div> */}
          </div>
          {isLoading ? (
            Array.from({ length: 10 }).map((_, index) => (
              <div key={index + 0}>
                <SkeletonLoader />
              </div>
            ))
          ) : (
            <>
              {initialData.map((item) => (
                <div
                  key={item.category_id}
                  className="grid grid-cols-[20px_repeat(3,1.3fr)_20px_1fr] items-center gap-4 border-b border-[#0075FF1A] px-[40px]"
                >
                  <div className="py-[0px] text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222]">
                    <input
                      type="checkbox"
                      name="is_top_collection"
                      checked={item.is_top_collection === true}
                      onChange={(event) => {
                        handleInputChange(item.category_id, event);
                      }}
                      className="block w-fit rounded-[10px] border border-[#0075FF1A] bg-white px-[12px] py-[15px] font-outfit text-[14px] font-normal leading-[17.64px] text-[#666666] outline-none"
                    />
                  </div>
                  <div className="py-[0px] text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222]">
                    {item.category_id}
                  </div>
                  <div className="py-[0px] text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222]">
                    {item.category_name}
                  </div>

                  <div className="max-w-[250px] py-[0px] text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222]">
                    <input
                      type="text"
                      name="category_order"
                      value={item.category_order}
                      onInput={(event) => {
                        event.target.value = event.target.value.replace(
                          /[^0-9]/g,
                          "",
                        );
                        handleInputChange(item.category_id, event);
                      }}
                      className="#FFFFFF block w-full rounded-[10px] border bg-[#F7FAFD] px-[12px] py-[11px] font-outfit text-[14px] font-normal leading-[17.64px] text-[#222222] outline-none"
                    />
                  </div>
                  <div className="cursor-pointer py-[16px] text-left">
                    <button
                      onClick={() => handleWarningModal(item.category_id)}
                      className="w-fit cursor-pointer text-left font-outfit text-[14px] font-normal leading-[17.64px] text-[#FC2D2D]"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                  <div className="h-full cursor-pointer py-[16px] text-left">
                    <Link
                      to={`photos?category_order=${item.category_id}&category_name=${item.category_name}`}
                      className="flex max-w-[20px] cursor-pointer items-center justify-center font-outfit text-[14px] font-normal leading-[17.64px] text-[#2DFCF3]"
                      onClick={() => handleSelectValue(item)}
                    >
                      <BrowseImageIcon />
                      {/* Browse Image */}
                    </Link>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
      <div className="absolute bottom-5 left-0 flex w-full justify-center">
        {/* <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        /> */}
      </div>
      <WarningModal
        isOpen={isWarningModalOpen}
        setIsOpen={setIsWarningModalOpen}
        onConfirm={handleDelete}
      />
      <DialogBox
        isOpen={isOpenDialog}
        onClose={handleCloseDialog}
        handleAddNewCategory={handleAddNewCategory}
      />
    </div>
  );
}
